import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';

type Status = 'verifying' | 'success' | 'expired' | 'invalid' | 'error';

/**
 * Landing page for the emailed verification link (deep-link only — never in Nav).
 * Consumes ?token= against POST /api/auth/verify-email and reports the outcome.
 * Re-runs per token (hash navigation keeps this component mounted); duplicate
 * POSTs are safe — the endpoint is idempotent for a verified account.
 */
export default function VerifyEmail() {
  const [params] = useSearchParams();
  const token = params.get('token') ?? '';
  const [status, setStatus] = useState<Status>(token ? 'verifying' : 'invalid');

  useEffect(() => {
    if (!token) {
      setStatus('invalid');
      return;
    }
    let cancelled = false;
    setStatus('verifying');
    (async () => {
      try {
        const res = await fetch('/api/auth/verify-email', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ token }),
        });
        const body = await res.json().catch(() => ({}));
        if (cancelled) return;
        if (res.ok && body.ok) setStatus('success');
        else if (res.status === 410) setStatus('expired');
        else if (res.status === 400) setStatus('invalid');
        else setStatus('error');
      } catch {
        if (!cancelled) setStatus('error');
      }
    })();
    return () => { cancelled = true; };
  }, [token]);

  const copy: Record<Status, { kicker: string; title: string; detail: string }> = {
    verifying: {
      kicker: '/ verifying',
      title: 'Checking your link…',
      detail: 'One moment while ELARIA confirms this email address.',
    },
    success: {
      kicker: '/ verified',
      title: 'Email verified.',
      detail: 'Your address is confirmed. You can head into the portal.',
    },
    expired: {
      kicker: '/ link expired',
      title: 'This link has expired.',
      detail: 'Verification links last 24 hours. Sign in to the portal and choose “Resend verification” to get a fresh one.',
    },
    invalid: {
      kicker: '/ link invalid',
      title: 'This link is not valid.',
      detail: 'It may have already been used, or the address was copied incompletely. Sign in to the portal to resend a fresh link.',
    },
    error: {
      kicker: '/ hiccup',
      title: 'Could not reach the server.',
      detail: 'Check your connection and reload this page — the link itself may still be good.',
    },
  };

  const { kicker, title, detail } = copy[status];

  return (
    <section className="container" style={{ textAlign: 'center', padding: '8rem 2rem' }}>
      <div style={{
        fontFamily: 'var(--font-mono)',
        fontSize: '0.7rem',
        letterSpacing: '0.28em',
        color: 'var(--cyan)',
        textTransform: 'uppercase',
        marginBottom: '1rem',
      }}>
        {kicker}
      </div>
      <h1 style={{ fontFamily: 'var(--font-display)', fontSize: '3rem', letterSpacing: '0.02em' }}>
        {title}
      </h1>
      <p style={{
        fontFamily: 'var(--font-serif)',
        fontSize: '1.2rem',
        color: 'var(--text-dim)',
        marginTop: '1rem',
        marginBottom: '2rem',
        maxWidth: '34rem',
        marginLeft: 'auto',
        marginRight: 'auto',
      }}>
        {detail}
      </p>
      {status !== 'verifying' && (
        <Link to={status === 'success' ? '/portal' : '/'} style={{
          display: 'inline-block',
          padding: '0.85rem 1.5rem',
          background: 'var(--cyan)',
          color: 'var(--navy)',
          fontFamily: 'var(--font-mono)',
          fontSize: '0.8rem',
          letterSpacing: '0.14em',
          textTransform: 'uppercase',
          borderRadius: 'var(--radius)',
          fontWeight: 600,
        }}>
          {status === 'success' ? 'Enter the portal →' : 'Back to home →'}
        </Link>
      )}
    </section>
  );
}
